import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { useStateSelector } from '../../../store/selectors';
import { LoadAnnouncements } from '../../../slices/announcements/load-announcements.thunk';
import { Announcement } from '../../../slices/announcements/announcements.model';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import {
    addNotificationFlagToDelete,
    removeNotificationFlagLocally,
} from '../../../slices/notification-flags/notification-flags.slice';
import AnnouncementModal from '../../../containers/UserPages/Login/Modals/AnnouncementModal';
import { QueryParams } from '../../../utils/queryParams';
import vetValueLogo from '../../../assets/demo-ui/images/vetValue.png';
import AnnouncementIcon from '../../../assets/icons/announcement.svg?react';
import './AnnouncementsMenuSection.scss';

const MAX_UNSEEN_TO_SHOW = 5;
const RECENT_FALLBACK_COUNT = 3;

export const AnnouncementsMenuSection = () => {
    const dispatch = useDispatch() as any;
    const announcements = useStateSelector((s) => s.announcements.announcements);
    const flags = useStateSelector((s) => s.notificationFlags.flags);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [singleAnnouncement, setSingleAnnouncement] =
        useState<Announcement | null>(null);
    const [searchParams] = useSearchParams();

    const popupData = singleAnnouncement ? [singleAnnouncement] : announcements;

    const openAll = () => {
        setSingleAnnouncement(null);
        setIsPopupVisible(true);
    };

    const openOne =
        (announcement: Announcement) => (e: React.MouseEvent) => {
            e.stopPropagation();
            setSingleAnnouncement(announcement);
            setIsPopupVisible(true);
        };

    const closePopup = () => {
        setIsPopupVisible(false);
        popupData.forEach((announcement) => {
            const eventCode = `${NotificationFlagsCategories.AnnouncementsCode}.${announcement.id}`;
            const flag = flags.find(
                (f) => f.eventCode.toLowerCase() === eventCode.toLowerCase()
            );
            if (flag) {
                // The popup was a genuine view — hide the flag right away
                // instead of waiting for the next flags refresh.
                dispatch(removeNotificationFlagLocally(flag.eventCode));
                dispatch(addNotificationFlagToDelete(flag));
            }
        });
        setSingleAnnouncement(null);
    };

    useEffect(() => {
        dispatch(LoadAnnouncements());
    }, [dispatch]);

    useEffect(() => {
        const isAnnouncementsOpened =
            searchParams.get(QueryParams.IsAnnouncementsOpened) === 'true';
        if (isAnnouncementsOpened) {
            setIsPopupVisible(true);
        }
    }, []);

    const isUnseen = (announcement: Announcement) =>
        flags.some(
            (f) =>
                f.eventCode.toLowerCase() ===
                `${NotificationFlagsCategories.AnnouncementsCode}.${announcement.id}`.toLowerCase()
        );

    const sorted = [...announcements].sort(
        (a, b) =>
            moment(b.postedOn, 'YYYY-MM-DD').valueOf() -
            moment(a.postedOn, 'YYYY-MM-DD').valueOf()
    );

    const unseen = sorted.filter(isUnseen);
    const displayed =
        unseen.length > RECENT_FALLBACK_COUNT
            ? unseen.slice(0, MAX_UNSEEN_TO_SHOW)
            : sorted.slice(0, RECENT_FALLBACK_COUNT);

    const popup = createPortal(
        <AnnouncementModal
            data={popupData}
            isVisible={isPopupVisible}
            closeAnnounceMent={closePopup}
        />,
        document.body
    );

    return (
        <>
            <div
                className="announcements-menu-section--compact"
                onClick={openAll}>
                <AnnouncementIcon className="announcement-section-icon" />
                <NotificationFlag
                    eventCode={NotificationFlagsCategories.AnnouncementsCode}
                    includePrefix
                    doNotRemove
                />
            </div>
            <div
                className="announcements-menu-section"
                onClick={openAll}>
                <div className="announcement-section-icon-column">
                    <AnnouncementIcon className="announcement-section-icon" />
                </div>
                <div className="announcement-section-body">
                    <div className="announcement-section-header">
                        <img
                            src={vetValueLogo}
                            alt="VetValue"
                            className="announcement-section-logo"
                        />
                        <h6 className="announcement-section-heading">
                            Announcements
                        </h6>
                        <NotificationFlag
                            eventCode={
                                NotificationFlagsCategories.AnnouncementsCode
                            }
                            includePrefix
                            doNotRemove
                        />
                    </div>
                    <div className="vat_value_content">
                        {displayed.map((announcement) => (
                            <div
                                key={announcement.id}
                                className="announcement-row"
                                onClick={openOne(announcement)}>
                                <span className="announcement-date">
                                    {moment(
                                        announcement.postedOn,
                                        'YYYY-MM-DD'
                                    ).format('MMM D, YYYY')}
                                </span>
                                <span className="announcement-title-wrapper">
                                    {announcement.title}
                                    {isUnseen(announcement) && (
                                        <span className="announcement-new-badge">
                                            NEW
                                        </span>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                    <button className="see-all-button">See all</button>
                </div>
            </div>
            {popup}
        </>
    );
};
